// Stations — a venue's machines (a PC, a console, a sim rig), each in one class and, optionally, one
// zone (F-ORG-03 rules 3 and 4; spec/13 §12, spec/04 §3).
//
//   GET   /v1/organizations/{o}/venues/{v}/stations              the venue's stations   stations:read
//   POST  /v1/organizations/{o}/venues/{v}/stations              define one             stations:manage
//   PATCH /v1/organizations/{o}/venues/{v}/stations/{stationId}  edit one               stations:manage
//   POST  …/stations/{stationId}/unpair                           unpair its PC          stations:manage
//   POST  …/stations/{stationId}/remove                           remove it              stations:manage
//
// Defining a station is the venue-side of adding a PC: the operator says which number, class and
// zone the machine is. Pairing a physical device to it — the pairing code, the device key, the
// offline secret — is F-STN-02 (#80) and is a separate step; a freshly defined station is `pairing`
// mode with no `deviceId` until then.
//
// **Managed or staff-run** (ADR-0033): a `pc` is managed — paired to the station app, leased, locked down. Every
// other kind is staff-run by default — no pairing, no agent; staff run its sessions from the console and the server
// owns the timer. `managed` overrides the kind's default either way.
//
// **Unpair and remove** (ADR-0057): unpairing revokes the PC's device key at once and sends it `station.unpair`, so it
// forgets its identity and shows a fresh pairing code; the station stays, waiting for a PC. Removing a station takes it
// out of the venue: it is unpaired the same way, and it leaves the floor, the Stations list and every picker, and its
// number is free for a new station. It is never deleted: its past sessions, sales and audit still name it. Both are
// refused while a session runs on the station.
//
// A station belongs to exactly one class (its price and permission tier) and takes that class's
// `defaultCapacity` unless it says otherwise. `number` is unique within the venue, so the same
// number in another venue is fine. The class and zone must be of the station's own venue.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { DeviceIdSchema, OrganizationIdSchema, StationClassIdSchema, StationIdSchema, VenueIdSchema, ZoneIdSchema } from "../ids/schema.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IdempotencyKeySchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { StationModeSchema } from "../realtime/station.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { STATIONS_MANAGE, STATIONS_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { MAX_STATION_CAPACITY } from "./station-classes.js";
/** spec/04 §3: what the machine is. `pc` is the common one; the pricing and library follow the class. */
export const STATION_KINDS = ['pc', 'console', 'sim', 'vr', 'table', 'other'];
export const StationKindSchema = z.enum(STATION_KINDS).openapi('StationKind', {
    description: 'What the machine is (spec/04 §3). Set when the station is defined and fixed thereafter.',
});
/**
 * What a paired device is for (F-STN-17, spec/10 §6.5, 2026-10-01): a `station` is a playable PC on the floor; a
 * `counter` is the venue's counter PC running **Clavian Counter** — the same installer with the counter role, which
 * applies no kiosk lockdown and keeps the venue's wake list so staff can wake every PC over the LAN with no cloud.
 * A counter is never bookable, never on the floor and never starts a session. The device declares its role when it
 * starts pairing (`DevicePairingStartRequest.role`); a station defined in the console is a `station`.
 */
export const STATION_ROLES = ['station', 'counter'];
export const StationRoleSchema = z.enum(STATION_ROLES).openapi('StationRole', {
    description: 'What the device is for: `station` — a playable PC on the floor; `counter` — the venue’s counter PC running Clavian Counter ' +
        '(no kiosk, not bookable, wakes the venue’s PCs over the LAN).',
});
/**
 * Which app a PC on the floor runs (ADR-0065 decision 1, F-STN-28, spec/13 §12): `station` expects the Clavian Station
 * app — today's behaviour and every existing station's value; `none` is a PC without it, in a venue with Clavian Boot and
 * not Clavian Stations. A `none` station is never billed or counted as a station seat, needs no pairing and has no
 * session, player or kiosk state; it is made only by the Floor's **Make it station** in such a venue.
 */
export const STATION_CLIENT_KINDS = ['station', 'none'];
export const StationClientKindSchema = z.enum(STATION_CLIENT_KINDS).openapi('StationClientKind', {
    description: 'Which app the PC runs: `station` — the Clavian Station app (it pairs and runs sessions); `none` — no Clavian Station ' +
        'app (a Clavian Boot only PC: no pairing, no sessions; its tile shows only how it starts).',
});
/**
 * A station's configured availability (spec/04 §3, spec/13 §12). `active` is on the floor and the
 * kiosk; `disabled` shows "out of order" with `disabledReason`. This is what staff set — distinct
 * from `mode`, the runtime state machine the station and the cloud drive (spec/10 §6.1).
 */
export const STATION_STATUSES = ['active', 'disabled'];
export const StationStatusSchema = z.enum(STATION_STATUSES).openapi('StationStatus', {
    description: 'Whether the station is available (`active`) or taken out of service (`disabled`).',
});
/** The largest station number a venue may give: room for floors of hundreds, not a typo of millions. */
export const MAX_STATION_NUMBER = 100000;
const numberSchema = z
    .int()
    .min(1)
    .max(MAX_STATION_NUMBER)
    .openapi({ description: 'The number on the tile. Unique within the venue; the same number in another venue is fine.' });
const nameSchema = z
    .string()
    .trim()
    .min(1)
    .max(60)
    .openapi({ description: 'An optional label beside the number, e.g. "Corner rig". A station may go by its number alone.' });
const capacitySchema = z
    .int()
    .min(1)
    .max(MAX_STATION_CAPACITY)
    .openapi({ description: 'Players this station seats. Defaults to the class’s `defaultCapacity` when a station is defined.' });
const managedSchema = z.boolean().openapi({
    description: 'Whether a device runs the station (`true`: a PC with the station app — paired, leased, locked down, health-reported) ' +
        'or staff run it from the console (`false`: no pairing, no agent, no lockdown, no offline or health warnings; the ' +
        'server owns its session timer and staff are alerted as time runs out). ADR-0033.',
});
const disabledReasonSchema = z
    .string()
    .trim()
    .max(200)
    .nullable()
    .openapi({ description: 'Shown on the kiosk when the station is `disabled`. Null when it is `active`.' });
export const StationSchema = z
    .object({
    id: StationIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    number: numberSchema,
    /** An optional label; null when the station goes by its number alone. */
    name: nameSchema.nullable(),
    kind: StationKindSchema,
    /** `station`, or `counter` for the venue's Clavian Counter PC (not playable, not on the floor). Read-only here. */
    role: StationRoleSchema,
    /** Managed (paired to the station app) or staff-run (ADR-0033). */
    managed: managedSchema,
    /**
     * Which app the PC runs (ADR-0065): `station` (the Clavian Station app; every station before this) or `none` (a
     * Clavian Boot only PC). Read-only here: a `none` station is made only by the Floor's Make it station.
     */
    client: StationClientKindSchema,
    classId: StationClassIdSchema,
    /** The zone the station is in, or null when it is not placed in one. */
    zoneId: ZoneIdSchema.nullable(),
    capacity: capacitySchema,
    status: StationStatusSchema,
    disabledReason: disabledReasonSchema,
    /** The runtime mode (spec/10 §6.1). `pairing` until a device is bound (F-STN-02). Read-only here. */
    mode: StationModeSchema,
    /** The paired device (`dev_…`, F-STN-05), or null until pairing binds one (F-STN-02). Read-only here. */
    deviceId: DeviceIdSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Station', { description: 'A machine in a venue: a PC, console, sim rig, VR set or table (spec/04 §3).' });
export const StationListSchema = paginated(StationSchema, 'StationList');
export const CreateStationSchema = z
    .object({
    number: numberSchema,
    name: nameSchema.optional(),
    kind: StationKindSchema,
    classId: StationClassIdSchema,
    /** Omit to leave the station unplaced; it can be placed in a zone later. */
    zoneId: ZoneIdSchema.optional(),
    /** Omit to take the class’s `defaultCapacity`. */
    capacity: capacitySchema.optional(),
    /** Omit for the kind’s default: a `pc` is managed (it pairs); every other kind is staff-run (ADR-0033). */
    managed: managedSchema.optional(),
})
    .strict()
    .openapi('CreateStation');
// `kind` is fixed at creation: a machine that becomes a different kind is a different station.
// Everything else the Stations screen edits is here; `status`/`disabledReason` are the disable and
// enable action (spec/13 §12), both under `stations:manage`. `managed` turns a station staff-run or back (ADR-0033):
// staff-run is refused with `409 conflict` while a device is paired to it.
export const UpdateStationSchema = z
    .object({
    number: numberSchema,
    name: nameSchema.nullable(),
    classId: StationClassIdSchema,
    zoneId: ZoneIdSchema.nullable(),
    capacity: capacitySchema,
    status: StationStatusSchema,
    disabledReason: disabledReasonSchema,
    managed: managedSchema,
})
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one field to change.' })
    .openapi('UpdateStation');
const StationParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, stationId: StationIdSchema })
    .openapi('StationParams');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const venueNotFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
// A class or zone that is not of this venue is a 400 with a field error on `classId` / `zoneId`.
const badReference = problemResponse('The class or zone is not one of this venue’s.', 'validation-failed');
const numberTaken = problemResponse('Another station at this venue already has that number.', 'conflict');
const numberTakenOrPaired = problemResponse('Another station at this venue already has that number, or the station has a device paired and cannot become staff-run.', 'conflict');
export const listStationsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations',
    operationId: 'listStations',
    tags: ['stations'],
    summary: "List a venue's stations",
    description: 'Oldest first. Requires `stations:read`.',
    [PERMISSION_EXTENSION]: STATIONS_READ,
    request: { params: VenueScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: { description: 'A page of stations.', content: { [JSON_CONTENT_TYPE]: { schema: StationListSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createStationRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations',
    operationId: 'createStation',
    tags: ['stations'],
    summary: 'Define a station',
    description: 'Adds a station in a class and, optionally, a zone. Takes the class’s `defaultCapacity` unless `capacity` is sent. ' +
        'Pairing a device to it is a separate step (F-STN-02). Requires `stations:manage`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateStationSchema } }, required: true },
    },
    responses: {
        201: { description: 'The station that was defined.', content: { [JSON_CONTENT_TYPE]: { schema: StationSchema } } },
        400: badReference,
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        409: numberTaken,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateStationRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}',
    operationId: 'updateStation',
    tags: ['stations'],
    summary: 'Edit a station',
    description: 'Only the fields sent change. Requires `stations:manage`. Audited with the before and after.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: {
        params: StationParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateStationSchema } }, required: true },
    },
    responses: {
        200: { description: 'The station as it is now.', content: { [JSON_CONTENT_TYPE]: { schema: StationSchema } } },
        400: badReference,
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such station at this venue.', 'not-found'),
        409: numberTakenOrPaired,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/**
 * The `Idempotency-Key` unpair and remove accept (spec/08 §2.2): a double-click or a retried request acts once. A retry
 * with the same key answers the first answer again (`Idempotent-Replayed: true`); the same key for a different request is
 * `422 idempotency-key-reused`. Optional: without one, a second remove finds no station (`404`) and a second unpair finds
 * no PC paired (`409`), so nothing happens twice either way.
 */
export const StationIdempotencyHeaderSchema = z
    .object({ 'idempotency-key': IdempotencyKeySchema.optional() })
    .openapi('StationIdempotencyHeaders');
/** What removing a station did (ADR-0057). The station itself is gone from every list, so this is all there is to show. */
export const StationRemovedSchema = z
    .object({
    id: StationIdSchema,
    /** The number it had, free now for a new station at the venue. */
    number: numberSchema,
    name: nameSchema.nullable(),
    removedAt: IsoDateTimeSchema,
    /** Whether a PC was paired to it: its device key was revoked and it was sent `station.unpair`. */
    unpaired: z.boolean(),
})
    .openapi('StationRemoved', { description: 'A station taken out of the venue (ADR-0057). Its history still names it.' });
const sessionRunningOrNotPaired = problemResponse('A session is running on this station (end it first), or no PC is paired to it.', 'conflict');
const sessionRunning = problemResponse('A session is running on this station: end it first.', 'conflict');
const keyReused = problemResponse('The Idempotency-Key was used for a different request.', 'idempotency-key-reused');
export const unpairStationRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/unpair',
    operationId: 'unpairStation',
    tags: ['stations'],
    summary: 'Unpair the PC from a station',
    description: 'The station keeps its number, class, zone and history and waits in `pairing` for a PC (ADR-0057). The PC that was paired ' +
        'to it has its device key revoked at once, so its next token is refused (spec/07 §5), and is sent `station.unpair`: a PC ' +
        'that is connected forgets its identity and shows a fresh pairing code. Refused with `409` while a session runs on the ' +
        'station, and for a station with no PC paired. Accepts an `Idempotency-Key`. Requires `stations:manage`. Audited ' +
        '(`stations.station.unpaired`).',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: { params: StationParamsSchema, headers: StationIdempotencyHeaderSchema },
    responses: {
        200: { description: 'The station as it is now: `pairing`, with no device.', content: { [JSON_CONTENT_TYPE]: { schema: StationSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such station at this venue.', 'not-found'),
        409: sessionRunningOrNotPaired,
        422: keyReused,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const removeStationRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/remove',
    operationId: 'removeStation',
    tags: ['stations'],
    summary: 'Remove a station',
    description: 'Takes the station out of the venue (ADR-0057). It leaves the floor, the floor plan, the Stations list and every picker, ' +
        'and its number is free for a new station; it is never deleted, so its past sessions, sales, reports and audit still name ' +
        'it. A PC paired to it is unpaired as `unpairStation` does: its device key is revoked at once and it is sent ' +
        '`station.unpair`. Refused with `409` while a session runs on the station. Accepts an `Idempotency-Key`. Requires ' +
        '`stations:manage`. Audited (`stations.station.removed`).',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: { params: StationParamsSchema, headers: StationIdempotencyHeaderSchema },
    responses: {
        200: { description: 'The station that was removed.', content: { [JSON_CONTENT_TYPE]: { schema: StationRemovedSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such station at this venue.', 'not-found'),
        409: sessionRunning,
        422: keyReused,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=stations.js.map